import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useSelector } from "react-redux";
import moment from "moment";
import { Add, ArrowUpward, BlockOutlined, CheckCircleOutline, LayersOutlined, PendingOutlined } from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { defaultAllSelection, scrollTop } from "services/constants";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { ApproveTable } from "pages/TeamZone/Approve/approveUi";
import { salaryTemplateListHeader } from "pages/PayRoll/PayRollHeaders";
import {
  GlobalBreadcrumb,
  GlobalConfirmDialog,
  sharedStickyFooterBarSx,
  sharedTableBodyCellSx,
  sharedTableHeadCellSx,
} from "components/DesignSystem/sharedUiPatterns";
import {
  APP_BAR_OFFSET,
  StatCard,
  designColors,
  designShell,
  designType,
  designWeights,
  paginationFooter,
  rowsPerPageOptions,
  withDesignFont,
} from "services/constants/designTokens";
import { ToneChip, VersionChip } from "../Shared/salaryUi";
import {
  PAY_BASES,
  PAY_BASIS_TONES,
  emptyTemplateForm,
  isOverridden,
  medianGross,
  templateToForm,
  toTemplateRow,
  versionLabel,
} from "./salaryTemplateData";
import {
  activateSalaryTemplate,
  checkTemplateName,
  createSalaryTemplate,
  deactivateSalaryTemplate,
  deleteSalaryTemplate,
  duplicateSalaryTemplate,
  getSalaryTemplate,
  getTemplateChoices,
  getTemplateComponents,
  getTemplatePreview,
  listSalaryTemplates,
  previewSalaryStructure,
  templateDetails,
  toComponentRows,
  updateSalaryTemplate,
} from "./salaryTemplateApi";
import {
  DuplicateTemplateDialog,
  SalaryTemplateFormPanel,
  SalaryTemplateViewPanel,
  TEMPLATE_STATUS_COLORS,
} from "./SalaryTemplatePanels";

// Payroll > Salary > Salary Templates, on the payroll/salary/ template APIs
// (salaryTemplateApi.js). Same layout as Salary Components and the Pay
// Inputs screens.

const ALL = "all";

// The cards the list API names: All, Used, Unused, Inactive.
const STATUS_CARDS = [
  { key: ALL, title: "All", detail: "Every template in this company", icon: LayersOutlined, accent: designColors.tealDark, tint: designColors.tealTint },
  { key: "Used", title: "In use", detail: "Assigned to employees. Changes save as a new version", icon: PendingOutlined, accent: "#F0954A", tint: "#FFF4E5" },
  { key: "Unused", title: "Unused", detail: "No employees yet. Edit or delete freely", icon: CheckCircleOutline, accent: "#12B76A", tint: "#E7F8EF" },
  { key: "Inactive", title: "Inactive", detail: "Hidden from Assign and Revise", icon: BlockOutlined, accent: designColors.slate600, tint: designColors.slate100 },
];

export const SalaryTemplate = (props) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Fresh state per company.
  return <SalaryTemplateScreen key={String(domain) + ":" + String(topbarCompanyId)} {...props} />;
};

const SalaryTemplateScreen = ({ subModuleId }) => {
  const { domain, userScreenAccess, userDetails, topbarCompanyName, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const canEdit = checkEditorPermission(userScreenAccess?.module_details, "Salary Configuration", "Salary Template");
  const validCompany =
    Number.isInteger(Number(topbarCompanyId)) && Number(topbarCompanyId) > 0 && Number(topbarCompanyId) !== defaultAllSelection;

  const [templates, setTemplates] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [choices, setChoices] = useState({ payBases: null, methods: null, defaultGross: {} });
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const mutationRef = useRef(null);

  const [statusCard, setStatusCard] = useState(ALL);
  const [basisTab, setBasisTab] = useState(ALL);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [viewId, setViewId] = useState(null);
  const [viewExpanded, setViewExpanded] = useState(true);
  const [formPanel, setFormPanel] = useState({ open: false, mode: "add", id: null, initial: null });
  // Add / Edit open as the full view; the user can collapse them.
  const [formExpanded, setFormExpanded] = useState(true);

  const tableContainerRef = useRef(null);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const footerRef = useCallback((node) => setFooterNode(node), []);

  useEffect(() => {
    if (!customFilterNode) return undefined;
    const update = () => setCustomFilterHeight(customFilterNode.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);

  useEffect(() => {
    if (!footerNode) return undefined;
    const update = () => setFooterHeight(footerNode.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  useEffect(() => {
    scrollTop(tableContainerRef);
  }, [page]);

  // ---- Loading ----

  useEffect(() => {
    const controller = new AbortController();
    if (!validCompany || !domain) {
      setTemplates([]);
      setLoading(false);
      return () => controller.abort();
    }
    setLoading(true);
    listSalaryTemplates(domain, topbarCompanyId, controller.signal)
      .then((rows) => { if (!controller.signal.aborted) setTemplates(rows); })
      // Errors show as a toast; the table stays empty.
      .catch((error) => { if (!controller.signal.aborted) { setTemplates([]); showToast("error", error.message); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [domain, topbarCompanyId, validCompany, refresh]);

  // Component picker for the builder (and names / versions for the rows).
  useEffect(() => {
    if (!validCompany || !domain) return undefined;
    const controller = new AbortController();
    getTemplateComponents(domain, topbarCompanyId, controller.signal)
      .then((rows) => { if (!controller.signal.aborted) setCatalog(rows); })
      .catch((error) => { if (!controller.signal.aborted) showToast("error", error.message); });
    return () => controller.abort();
  }, [domain, topbarCompanyId, validCompany, refresh]);

  useEffect(() => {
    if (!domain) return undefined;
    const controller = new AbortController();
    // Without them the form falls back to its own lists.
    getTemplateChoices(domain, controller.signal)
      .then((value) => { if (!controller.signal.aborted) setChoices(value); })
      .catch(() => {});
    return () => controller.abort();
  }, [domain]);

  useEffect(() => () => mutationRef.current?.abort(), []);

  const handleSavedViewChange = useCallback(() => setPage(0), []);
  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "salary_template_saved_views",
    fullHeaders: salaryTemplateListHeader,
    screenAccess: canEdit,
    onViewChange: handleSavedViewChange,
  });

  // ---- Data pipeline: pay-basis tab -> status card -> CustomFilter ----
  // Same order as the Pay Inputs screens: the tabs above the cards pick the
  // rows first, and the cards count only that tab's rows.

  const allRows = useMemo(() => templates.map((template) => toTemplateRow(catalog, template)), [templates, catalog]);
  const basisRows = useMemo(
    () => (basisTab === ALL ? allRows : allRows.filter((row) => row.pay_basis === basisTab)),
    [allRows, basisTab],
  );
  const statusCounts = useMemo(
    () => ({
      [ALL]: basisRows.length,
      Used: basisRows.filter((row) => row.status === "Used").length,
      Unused: basisRows.filter((row) => row.status === "Unused").length,
      Inactive: basisRows.filter((row) => row.status === "Inactive").length,
    }),
    [basisRows],
  );
  const tabRows = useMemo(
    () => (statusCard === ALL ? basisRows : basisRows.filter((row) => row.status === statusCard)),
    [basisRows, statusCard],
  );

  // CustomFilter reports its result one render after tabRows changes; only
  // trust it when it was computed from the current tabRows (Users pattern).
  const [filteredRows, setFilteredRows] = useState(null);
  const filteredSourceRef = useRef(null);
  const visibleRows = filteredRows !== null && filteredSourceRef.current === tabRows ? filteredRows : tabRows;
  const handleFilteredDataChange = useCallback(
    (data) => {
      filteredSourceRef.current = tabRows;
      setFilteredRows(data);
      if (page * rowsPerPage >= data.length) setPage(0);
    },
    [page, rowsPerPage, tabRows],
  );
  useEffect(() => setPage(0), [statusCard, basisTab]);
  const pageRows = visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [topbarCompanyName],
  );

  // ---- View: the open template's rows, preview, employees and versions ----

  const [details, setDetails] = useState({ id: null, template: null, preview: null, loading: false });
  const [detailsRefresh, setDetailsRefresh] = useState(0);
  const [viewGross, setViewGross] = useState("");
  const [revisePrompts, setRevisePrompts] = useState({});
  const listTemplate = (id) => templates.find((item) => String(item.id) === String(id)) ?? null;
  const viewListTemplate = viewId == null ? null : listTemplate(viewId);
  const viewListed = Boolean(viewListTemplate);
  useEffect(() => {
    if (viewId == null || !viewListTemplate) {
      setDetails({ id: null, template: null, preview: null, loading: false });
      return undefined;
    }
    const controller = new AbortController();
    setDetails((current) => ({ ...current, id: viewId, template: String(current.id) === String(viewId) ? current.template : null, loading: true }));
    templateDetails(domain, viewListTemplate, controller.signal)
      .then(({ template, preview, errors }) => {
        if (controller.signal.aborted) return;
        setDetails({ id: viewId, template, preview, loading: false });
        setViewGross(String(medianGross(template, choices.defaultGross)));
        if (errors.length) showToast("error", errors[0]);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setDetails({ id: viewId, template: null, preview: null, loading: false });
        showToast("error", error.message);
      });
    return () => controller.abort();
    // The list row object changes with every list load; detailsRefresh
    // reloads after a change instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, viewId, viewListed, detailsRefresh]);

  const withDetails = (template) =>
    template && details.template && String(details.id) === String(template.id) ? { ...template, ...details.template } : template;
  const viewTemplate = withDetails(viewListTemplate);
  // A duplicate opened for review may not be in the list yet: the form then
  // uses the template it was opened with.
  const formTemplate = withDetails(formPanel.id == null ? null : listTemplate(formPanel.id)) ?? formPanel.template ?? null;
  useEffect(() => {
    // The view opens full width each time; collapsing lasts until it closes.
    if (!viewTemplate) setViewExpanded(true);
  }, [viewTemplate]);

  // Server preview for the open template (template-preview, template_id
  // only), loaded once per opened template.
  useEffect(() => {
    if (!details.template || details.loading) return undefined;
    const controller = new AbortController();
    const lines = details.template.versions[details.template.versions.length - 1].lines;
    getTemplatePreview(domain, details.template.id, lines, controller.signal)
      .then((preview) => { if (!controller.signal.aborted && preview) setDetails((current) => ({ ...current, preview })); })
      .catch(() => {});
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, details.template?.id, details.loading]);

  // ---- Actions ----

  // One save / status change at a time; errors show as a toast.
  const runAction = async (action) => {
    if (busyRef.current || !validCompany || !canEdit) return { success: false };
    busyRef.current = true;
    setBusy(true);
    const controller = new AbortController();
    mutationRef.current = controller;
    try {
      const value = await action(controller.signal);
      if (controller.signal.aborted) return { success: false };
      return { success: true, value };
    } catch (error) {
      if (!controller.signal.aborted) showToast("error", error.message);
      return { success: false };
    } finally {
      if (!controller.signal.aborted) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };

  const reload = () => {
    setRefresh((value) => value + 1);
    setDetailsRefresh((value) => value + 1);
  };

  const openForm = (mode, id, initial, template = null) => {
    setFormExpanded(true);
    setFormPanel({ open: true, mode, id, initial, template });
  };
  const closeForm = () => setFormPanel((current) => ({ ...current, open: false }));

  const rowsFor = useCallback((lines) => toComponentRows(lines, (line) => isOverridden(catalog, line)), [catalog]);
  const handlePreview = useCallback(
    (lines, gross, signal) => previewSalaryStructure(domain, topbarCompanyId, gross, rowsFor(lines), lines, signal),
    [domain, topbarCompanyId, rowsFor],
  );

  // Returns { fieldErrors } when the name check refuses.
  const handleSave = async (form, { newVersion, used, previewGross }) => {
    const adding = formPanel.mode === "add";
    const editingId = formPanel.id;
    const rows = rowsFor(form.lines);
    const result = await runAction(async (signal) => {
      const nameError = await checkTemplateName(domain, topbarCompanyId, form.name, adding ? undefined : editingId, signal);
      if (nameError) return { fieldErrors: { name: nameError } };
      if (adding) return createSalaryTemplate(domain, topbarCompanyId, form, rows, previewGross, signal);
      return { id: editingId, ...(await updateSalaryTemplate(domain, editingId, form, rows, { newVersion, used, previewGross }, signal)) };
    });
    if (!result.success) return undefined;
    if (result.value.fieldErrors) return result.value;
    // Add goes back to the list; Edit (started from the view) returns to it.
    closeForm();
    if (adding) setViewId(null);
    reload();
    if (adding) {
      showToast("success", result.value.message || `${form.name.trim()} created as V1.`);
      return undefined;
    }
    const nextLabel = formTemplate ? versionLabel(formTemplate.versions[formTemplate.versions.length - 1].version + 1) : "";
    showToast(
      "success",
      result.value.message || (newVersion ? `${form.name.trim()} saved as ${nextLabel}.` : `${form.name.trim()} updated.`),
    );
    // Employees still on older versions: offer the revision (revise_prompt).
    if (newVersion && (result.value.employeesOnPrevious || result.value.revisePrompt)) {
      setRevisePrompts((current) => ({
        ...current,
        [editingId]:
          result.value.revisePrompt ||
          `${result.value.employeesOnPrevious} employees are on an older version. Run a Template-Based Revision to move them to ${nextLabel}.`,
      }));
    }
    return undefined;
  };

  // Duplicate as…: the server makes a new Unused V1. When the pay basis
  // changed, its fixed-amount rows come back to re-check: the new
  // template's form opens with them highlighted.
  const [duplicateFrom, setDuplicateFrom] = useState(null);
  const handleDuplicate = async (name, payBasis) => {
    const source = duplicateFrom;
    const result = await runAction((signal) => duplicateSalaryTemplate(domain, source.id, name, payBasis, signal));
    if (!result.success) return;
    setDuplicateFrom(null);
    const { id, reviewCodes, message } = result.value;
    reload();
    if (!reviewCodes.length || id == null) {
      showToast("success", message || `${name.trim()} created as V1.`);
      setViewId(null);
      return;
    }
    showToast("success", message || `${name.trim()} created as V1. Check the highlighted amounts.`);
    const loaded = await runAction((signal) => getSalaryTemplate(domain, id, signal));
    if (!loaded.success) return;
    setViewId(null);
    openForm("edit", id, templateToForm(loaded.value.template, { reviewCodes }, catalog), loaded.value.template);
  };

  // True when the server took it, so the dialog can close.
  const handleDeactivate = async (template, reason) => {
    const result = await runAction((signal) => deactivateSalaryTemplate(domain, template.id, reason, signal));
    if (!result.success) return false;
    reload();
    showToast("success", result.value || `${template.name} deactivated.`);
    return true;
  };

  const handleReactivate = async (template) => {
    const result = await runAction((signal) => activateSalaryTemplate(domain, template.id, signal));
    if (!result.success) return;
    reload();
    showToast("success", result.value || `${template.name} activated.`);
  };

  // Delete (Unused templates only; the server answers 400 for a used one).
  const [deleteTarget, setDeleteTarget] = useState(null);
  const handleDelete = async () => {
    const template = deleteTarget;
    if (!template) return;
    const result = await runAction((signal) => deleteSalaryTemplate(domain, template.id, signal));
    if (!result.success) return;
    setDeleteTarget(null);
    setViewId(null);
    reload();
    showToast("success", result.value || `${template.name} deleted.`);
  };

  // A newly added template (in the list but not loaded) is opened from the
  // list row; Edit needs the rows, so it uses the loaded template.
  const openEdit = (template, upgradeCode) => openForm("edit", template.id, templateToForm(template, { upgradeCode }, catalog));

  // ---- Table ----

  const NUMERIC = new Set(["employees"]);
  const renderCell = (row, key) => {
    switch (key) {
      case "name":
        return (
          <Mui.Box sx={{ minWidth: 0 }}>
            <Mui.Typography noWrap sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium }}>{row.name}</Mui.Typography>
            <Mui.Typography noWrap sx={designType.captionText}>{row.description}</Mui.Typography>
          </Mui.Box>
        );
      case "pay_basis":
        return <ToneChip label={row.pay_basis} tone={PAY_BASIS_TONES[row.pay_basis]} />;
      case "components":
        return (
          <Mui.Box sx={{ minWidth: 0 }}>
            <Mui.Typography noWrap sx={designType.tableBodyCell}>{row.components} component{row.components === 1 ? "" : "s"}</Mui.Typography>
            {row.components_detail ? <Mui.Typography noWrap sx={designType.captionText}>{row.components_detail}</Mui.Typography> : null}
          </Mui.Box>
        );
      case "version":
        return <VersionChip label={row.version} />;
      case "employees":
        return row.employees || "—";
      case "updates_label":
        return row.updates && row.active ? (
          <ToneChip label={<><ArrowUpward sx={{ fontSize: 13, verticalAlign: "-2px" }} /> {row.updates_label}</>} tone="blue" />
        ) : (
          <Mui.Box component="span" sx={{ color: designColors.slate400 }}>—</Mui.Box>
        );
      case "status":
        return <RequestStatusChip status={row.status} colorCode={TEMPLATE_STATUS_COLORS[row.status]} />;
      default:
        return row[key] || "–";
    }
  };

  const headCellSx = { ...sharedTableHeadCellSx, top: APP_BAR_OFFSET + customFilterHeight, whiteSpace: "nowrap" };
  const basisTabs = [
    { value: ALL, label: "All", count: allRows.length },
    ...PAY_BASES.map((basis) => ({ value: basis, label: basis, count: allRows.filter((row) => row.pay_basis === basis).length })),
  ];

  return (
    <ThemeProvider theme={withDesignFont}>
      <Mui.Box sx={{ ...designShell.pageSurface, pb: 2 }}>
        <Mui.Box sx={{ ...designShell.headerRowBase, alignItems: { xs: "flex-start", sm: "center" }, px: 2, pt: 2, mb: 2 }}>
          <Mui.Box sx={{ minWidth: 0 }}>
            <GlobalBreadcrumb trail={["Payroll", "Salary", "Salary Templates"]} />
            <Mui.Typography component="h1" sx={designType.pageTitle}>Salary Templates</Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              Reusable groups of components. Assign and Revise copy a template into each employee's salary.
            </Mui.Typography>
          </Mui.Box>
          {canEdit ? (
            <Mui.Button
              startIcon={<Add />}
              onClick={() => openForm("add", null, { ...emptyTemplateForm(catalog), review_codes: [] })}
              disabled={busy || loading || !validCompany}
              sx={designShell.primaryButtonBase}
            >
              Add Template
            </Mui.Button>
          ) : null}
        </Mui.Box>

        <Mui.Box sx={{ px: 2 }}>
          {/* Pay-basis tabs above the cards (same look as the Pay Inputs
              source tabs): the cards and the table follow the selected tab. */}
          <Mui.Box sx={{ ...designShell.sectionCard, px: 2, mb: 1.5 }}>
            <Mui.Tabs
              value={basisTab}
              onChange={(event, next) => setBasisTab(next)}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={designShell.panelTabs}
            >
              {basisTabs.map((tab) => (
                <Mui.Tab key={tab.value} value={tab.value} label={`${tab.label} (${tab.count.toLocaleString("en-IN")})`} />
              ))}
            </Mui.Tabs>
          </Mui.Box>

          <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 1.5, mb: 2 }}>
            {STATUS_CARDS.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                title={card.title}
                value={statusCounts[card.key]}
                detail={card.detail}
                accent={card.accent}
                tint={card.tint}
                onClick={() => setStatusCard(card.key)}
                isActive={statusCard === card.key}
                activeBorder
              />
            ))}
          </Mui.Box>

          <Mui.Box ref={customFilterRef} sx={{ position: "sticky", top: APP_BAR_OFFSET, zIndex: 4, backgroundColor: designColors.white }}>
            <CustomFilter
              title="Salary Templates"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={tabRows}
              filteredData={visibleRows}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={canEdit}
              hideCount={true}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={reportMetadata}
              labeledToolbar={true}
              searchPlaceholder="Search template name"
            />
          </Mui.Box>

          <Mui.Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
            <Mui.TableContainer ref={tableContainerRef} sx={{ width: "100%", overflowX: "visible", pb: footerHeight ? `${footerHeight}px` : 0 }}>
              <ApproveTable stickyHeader>
                <Mui.TableHead sx={designShell.tableStickyHead}>
                  <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                    {visibleHeaders?.map((head) => (
                      <Mui.TableCell key={head.key} align={NUMERIC.has(head.key) ? "right" : "left"} sx={headCellSx}>
                        {head.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {loading ? (
                    <Mui.TableRow>
                      <Mui.TableCell colSpan={visibleHeaders?.length ?? 1} sx={{ borderBottom: "none" }}>
                        <Loader info="Loading..." minHeight="40vh" />
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ) : visibleRows.length === 0 ? (
                    <TableEmptyState dataLength={tabRows} colSpan={visibleHeaders?.length ?? 1} />
                  ) : (
                    pageRows.map((row) => (
                      <Mui.TableRow
                        key={row.id}
                        hover
                        onClick={() => setViewId(row.id)}
                        sx={{
                          cursor: "pointer",
                          ...designShell.tableRowHover,
                          ...(row.active ? {} : { "& td": { color: designColors.slate500 }, opacity: 0.75 }),
                          "&:last-child td": { borderBottom: "none" },
                        }}
                      >
                        {visibleHeaders.map((head) => (
                          <Mui.TableCell
                            key={head.key}
                            align={NUMERIC.has(head.key) ? "right" : "left"}
                            sx={{ ...sharedTableBodyCellSx, py: 1.25, maxWidth: head.key === "name" || head.key === "components" ? 320 : undefined }}
                          >
                            {renderCell(row, head.key)}
                          </Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                    ))
                  )}
                </Mui.TableBody>
              </ApproveTable>
            </Mui.TableContainer>

            <Mui.Box ref={footerRef} sx={sharedStickyFooterBarSx}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: visibleRows.length,
                itemLabel: "templates",
                onPageChange: setPage,
                onRowsPerPageChange: (event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                },
                rowsPerPageOptions,
              })}
            </Mui.Box>
          </Mui.Box>
        </Mui.Box>
      </Mui.Box>

      <SalaryTemplateViewPanel
        open={Boolean(viewTemplate)}
        template={viewTemplate}
        catalog={catalog}
        canEdit={canEdit}
        busy={busy}
        detailsLoading={details.loading && !details.template}
        serverPreview={details.preview}
        gross={viewGross}
        onGrossChange={setViewGross}
        revisePrompt={viewTemplate ? revisePrompts[viewTemplate.id] : ""}
        // Only while open: GlobalSidePanel keeps an expanded panel showing
        // even after `open` turns false.
        expanded={Boolean(viewTemplate) && viewExpanded}
        onToggleExpand={() => setViewExpanded((value) => !value)}
        onClose={() => setViewId(null)}
        onEdit={(template) => openEdit(template)}
        onUpgrade={(template, code) => openEdit(template, code)}
        onDuplicate={setDuplicateFrom}
        onDeactivate={handleDeactivate}
        onReactivate={handleReactivate}
        onDelete={setDeleteTarget}
      />

      <SalaryTemplateFormPanel
        open={formPanel.open}
        mode={formPanel.mode}
        initial={formPanel.initial}
        template={formTemplate}
        templates={templates}
        catalog={catalog}
        payBases={choices.payBases}
        defaultGross={choices.defaultGross}
        busy={busy}
        expanded={formPanel.open && formExpanded}
        onToggleExpand={() => setFormExpanded((value) => !value)}
        onClose={closeForm}
        onSave={handleSave}
        onPreview={handlePreview}
      />

      <DuplicateTemplateDialog
        open={Boolean(duplicateFrom)}
        template={duplicateFrom}
        payBases={choices.payBases}
        busy={busy}
        onCancel={() => setDuplicateFrom(null)}
        onConfirm={handleDuplicate}
      />

      <GlobalConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete template"
        onCancel={() => { if (!busy) setDeleteTarget(null); }}
        onConfirm={handleDelete}
        confirmLabel="Delete"
        cancelLabel="Keep"
        loading={busy}
        loadingLabel="Deleting..."
        maxWidth="sm"
        danger
      >
        Delete {deleteTarget?.name}? No employee has it. This can't be undone here.
      </GlobalConfirmDialog>
    </ThemeProvider>
  );
};
